import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API, BACKEND_URL } from '@/App';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Trophy, Plus, Medal, Clock, Trash2, Crown, Award, Settings, Dumbbell, User, TrendingDown, TrendingUp } from 'lucide-react';

function HallOfFame() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [hofEntrenos, setHofEntrenos] = useState([]);
  const [usuarios, setUsuarios] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showManageEntrenosDialog, setShowManageEntrenosDialog] = useState(false);
  const [selectedEntreno, setSelectedEntreno] = useState('all');
  const [activeTab, setActiveTab] = useState('ranking');
  
  const [formData, setFormData] = useState({
    user_id: '',
    entreno_id: '',
    tiempo: '',
    fecha: new Date().toISOString().split('T')[0],
    notas: ''
  });
  
  const [newEntrenoName, setNewEntrenoName] = useState('');
  const [newEntrenoType, setNewEntrenoType] = useState('tiempo');

  const token = localStorage.getItem('token');
  const isAdmin = user?.role === 'entrenador';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recordsRes, entrenosRes, usersRes] = await Promise.all([
        axios.get(`${API}/hall-of-fame`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hall-of-fame/entrenos`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users/public`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setRecords(recordsRes.data);
      setHofEntrenos(entrenosRes.data);
      
      const usersMap = {};
      usersRes.data.forEach(u => { usersMap[u.id] = u; });
      setUsuarios(usersMap);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        entreno_id: formData.entreno_id,
        tiempo: formData.tiempo,
        fecha: formData.fecha,
        notas: formData.notas || null
      };
      
      if (isAdmin && formData.user_id && formData.user_id !== "self") {
        payload.user_id = formData.user_id;
      }
      
      await axios.post(`${API}/hall-of-fame`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchData();
      setShowAddDialog(false);
      setFormData({
        user_id: '',
        entreno_id: '',
        tiempo: '',
        fecha: new Date().toISOString().split('T')[0],
        notas: ''
      });
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddEntreno = async (e) => {
    e.preventDefault();
    if (!newEntrenoName.trim()) return;
    
    try {
      await axios.post(`${API}/hall-of-fame/entrenos`, {
        nombre: newEntrenoName,
        tipo: newEntrenoType,
        descripcion: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchData();
      setNewEntrenoName('');
      setNewEntrenoType('tiempo');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteEntreno = async (entrenoId) => {
    if (!window.confirm('¿Eliminar este entreno? Solo se puede si no tiene récords asociados.')) return;
    try {
      await axios.delete(`${API}/hall-of-fame/entrenos/${entrenoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchData();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('¿Eliminar este récord?')) return;
    try {
      await axios.delete(`${API}/hall-of-fame/${recordId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchData();
    } catch (error) {
      alert('Error al eliminar: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Filtrar records del usuario actual
  const myRecords = records.filter(r => r.user_id === user?.id);
  
  // Agrupar mis records por entreno
  const myRecordsByEntreno = myRecords.reduce((acc, record) => {
    const entrenoId = record.entreno_id;
    if (!acc[entrenoId]) {
      acc[entrenoId] = {
        nombre: record.entreno_nombre,
        records: []
      };
    }
    acc[entrenoId].records.push(record);
    return acc;
  }, {});

  // Ordenar cada grupo por fecha (más reciente primero)
  Object.values(myRecordsByEntreno).forEach(grupo => {
    grupo.records.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  });

  // Filtrar por entreno seleccionado para ranking
  const filteredRecords = selectedEntreno === 'all' 
    ? records 
    : records.filter(r => r.entreno_id === selectedEntreno);

  // Ordenar según el tipo de entreno
  const sortedRecords = [...filteredRecords].sort((a, b) => {
    // Si estamos viendo todos, ordenar por fecha (más reciente primero)
    if (selectedEntreno === 'all') {
        // Primero por fecha
        const dateDiff = new Date(b.fecha) - new Date(a.fecha);
        if (dateDiff !== 0) return dateDiff;
        // Desempate por creación
        return (b.fecha_creacion || '').localeCompare(a.fecha_creacion || '');
    }

    const entreno = hofEntrenos.find(e => e.id === selectedEntreno);
    const isReps = entreno?.tipo === 'reps';
    
    const valA = parseTime(a.tiempo);
    const valB = parseTime(b.tiempo);
    
    if (isReps) {
        return valB - valA; // Mayor es mejor
    } else {
        return valA - valB; // Menor es mejor
    }
  });

  function parseTime(timeStr) {
    if (!timeStr) return Infinity;
    
    // Normalizar el string: quitar espacios y convertir comas a puntos
    const normalized = timeStr.trim().replace(',', '.');
    
    // Separar por ":" para obtener las partes
    const parts = normalized.split(':');
    
    if (parts.length === 3) {
      // Formato HH:MM:SS o HH:MM:SS.ms
      const hours = parseFloat(parts[0]) || 0;
      const mins = parseFloat(parts[1]) || 0;
      const secs = parseFloat(parts[2]) || 0;
      return hours * 3600 + mins * 60 + secs;
    } else if (parts.length === 2) {
      // Formato MM:SS o MM:SS.ms
      const mins = parseFloat(parts[0]) || 0;
      const secs = parseFloat(parts[1]) || 0;
      return mins * 60 + secs;
    } else if (parts.length === 1) {
      // Solo segundos (ej: "45.5")
      return parseFloat(parts[0]) || Infinity;
    }
    
    return Infinity;
  }

  function formatPosition(index) {
    if (index === 0) return { icon: Crown, color: 'text-yellow-400', bg: 'bg-yellow-900/30' };
    if (index === 1) return { icon: Medal, color: 'text-gray-300', bg: 'bg-gray-700/30' };
    if (index === 2) return { icon: Award, color: 'text-amber-600', bg: 'bg-amber-900/30' };
    return { icon: null, color: 'text-gray-400', bg: 'bg-gray-800/30' };
  }

  // Calcular mejora entre dos tiempos
  function calcularMejora(tiempoAnterior, tiempoActual) {
    const anterior = parseTime(tiempoAnterior);
    const actual = parseTime(tiempoActual);
    if (anterior === Infinity || actual === Infinity) return null;
    const diff = anterior - actual;
    if (diff > 0) return { mejora: true, segundos: diff };
    if (diff < 0) return { mejora: false, segundos: Math.abs(diff) };
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-mk flex items-center justify-center">
        <div className="text-gold">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-mk p-4 pb-20" data-testid="hall-of-fame-page">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            Volver
          </Button>
          
          <div className="flex gap-2">
            {isAdmin && (
              <Button
                variant="outline"
                onClick={() => setShowManageEntrenosDialog(true)}
                className="border-gray-700 text-gray-300"
                size="sm"
              >
                <Settings className="h-4 w-4" />
              </Button>
            )}
            <Button
              onClick={() => setShowAddDialog(true)}
              className="btn-primary-mk"
              disabled={hofEntrenos.length === 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Añadir Tiempo
            </Button>
          </div>
        </div>

        <div className="text-center mb-4">
          <h1 className="text-3xl font-bold text-gold flex items-center justify-center gap-2">
            <Trophy className="h-8 w-8" />
            Hall of Fame
          </h1>
        </div>

        {/* Mensaje si no hay entrenos */}
        {hofEntrenos.length === 0 && (
          <Card className="card-mk border-yellow-800 bg-yellow-900/20">
            <CardContent className="p-4 text-center">
              <Dumbbell className="h-8 w-8 mx-auto text-yellow-500 mb-2" />
              <p className="text-yellow-400">No hay entrenos configurados</p>
              {isAdmin && (
                <Button
                  onClick={() => setShowManageEntrenosDialog(true)}
                  className="mt-3 btn-primary-mk"
                >
                  Configurar Entrenos
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        {hofEntrenos.length > 0 && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-gray-800">
              <TabsTrigger value="ranking" className="data-[state=active]:bg-gold data-[state=active]:text-black">
                <Trophy className="h-4 w-4 mr-2" />
                Ranking Equipo
              </TabsTrigger>
              <TabsTrigger value="personal" className="data-[state=active]:bg-gold data-[state=active]:text-black">
                <User className="h-4 w-4 mr-2" />
                Mis Tiempos
              </TabsTrigger>
            </TabsList>

            {/* Tab Ranking */}
            <TabsContent value="ranking" className="space-y-4 mt-4">
              {/* Filtro por entreno */}
              <div className="flex justify-center">
                <Select value={selectedEntreno} onValueChange={setSelectedEntreno}>
                  <SelectTrigger className="w-64 bg-gray-800 border-gray-700 text-white">
                    <SelectValue placeholder="Todos los entrenos" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="all" className="text-white">Todos los entrenos</SelectItem>
                    {hofEntrenos.map(e => (
                      <SelectItem key={e.id} value={e.id} className="text-white">{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Lista de récords */}
              <div className="space-y-3">
                {sortedRecords.length === 0 ? (
                  <Card className="card-mk border-gray-800">
                    <CardContent className="p-8 text-center">
                      <Trophy className="h-12 w-12 mx-auto text-gray-700 mb-3" />
                      <p className="text-gray-400">No hay récords registrados</p>
                    </CardContent>
                  </Card>
                ) : (
                  sortedRecords.map((record, index) => {
                    const pos = formatPosition(index);
                    const usuario = usuarios[record.user_id];
                    const canDelete = isAdmin || record.user_id === user?.id;
                    const isMe = record.user_id === user?.id;
                    
                    return (
                      <Card 
                        key={record.id} 
                        className={`card-mk border-gray-800 ${pos.bg} ${isMe ? 'ring-1 ring-gold' : ''}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${pos.color} ${index < 3 ? 'text-xl' : 'text-lg bg-gray-800'}`}>
                                {pos.icon ? <pos.icon className="h-6 w-6" /> : index + 1}
                              </div>
                              
                              <div className="flex items-center gap-3">
                                {usuario?.foto_perfil ? (
                                  <img
                                    src={`${BACKEND_URL}${usuario.foto_perfil}`}
                                    alt={usuario?.nombre}
                                    className="w-10 h-10 rounded-full object-cover border-2 border-gray-700"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-gold flex items-center justify-center font-bold text-gray-900">
                                    {usuario?.nombre?.charAt(0).toUpperCase() || '?'}
                                  </div>
                                )}
                                <div>
                                  <p className="text-white font-medium">
                                    {usuario?.nombre || 'Usuario'}
                                    {isMe && <span className="text-gold text-xs ml-2">(Tú)</span>}
                                  </p>
                                  <p className="text-xs text-gray-400">{record.entreno_nombre}</p>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className={`text-2xl font-bold ${index === 0 ? 'text-gold' : 'text-white'}`}>
                                  {record.tiempo}
                                </p>
                                <p className="text-xs text-gray-400">{record.fecha}</p>
                              </div>
                              
                              {canDelete && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(record.id)}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </TabsContent>

            {/* Tab Personal */}
            <TabsContent value="personal" className="space-y-4 mt-4">
              {myRecords.length === 0 ? (
                <Card className="card-mk border-gray-800">
                  <CardContent className="p-8 text-center">
                    <Clock className="h-12 w-12 mx-auto text-gray-700 mb-3" />
                    <p className="text-gray-400">No tienes tiempos registrados</p>
                    <Button
                      onClick={() => setShowAddDialog(true)}
                      className="mt-4 btn-primary-mk"
                    >
                      Añadir mi primer tiempo
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                Object.entries(myRecordsByEntreno).map(([entrenoId, data]) => {
                  const mejorTiempo = data.records.reduce((best, r) => {
                    const tiempo = parseTime(r.tiempo);
                    return tiempo < parseTime(best) ? r.tiempo : best;
                  }, data.records[0].tiempo);
                  
                  return (
                    <Card key={entrenoId} className="card-mk border-gray-800">
                      <CardContent className="p-4">
                        {/* Header del entreno */}
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-800">
                          <div>
                            <h3 className="text-lg font-semibold text-white">{data.nombre}</h3>
                            <p className="text-xs text-gray-400">{data.records.length} registro(s)</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-400">Mejor tiempo</p>
                            <p className="text-xl font-bold text-gold">{mejorTiempo}</p>
                          </div>
                        </div>
                        
                        {/* Lista de tiempos */}
                        <div className="space-y-2">
                          {data.records.map((record, idx) => {
                            const mejora = idx < data.records.length - 1 
                              ? calcularMejora(data.records[idx + 1].tiempo, record.tiempo)
                              : null;
                            const esMejor = record.tiempo === mejorTiempo;
                            
                            return (
                              <div 
                                key={record.id}
                                className={`flex items-center justify-between p-3 rounded-lg ${esMejor ? 'bg-gold/10 border border-gold/30' : 'bg-gray-800'}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="text-center w-16">
                                    <p className="text-xs text-gray-400">{record.fecha}</p>
                                  </div>
                                  <div className={`text-lg font-semibold ${esMejor ? 'text-gold' : 'text-white'}`}>
                                    {record.tiempo}
                                    {esMejor && <Trophy className="h-4 w-4 inline ml-2 text-gold" />}
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-3">
                                  {mejora && (
                                    <span className={`text-sm flex items-center gap-1 ${mejora.mejora ? 'text-green-400' : 'text-red-400'}`}>
                                      {mejora.mejora ? (
                                        <TrendingDown className="h-4 w-4" />
                                      ) : (
                                        <TrendingUp className="h-4 w-4" />
                                      )}
                                      {mejora.segundos}s
                                    </span>
                                  )}
                                  
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(record.id)}
                                    className="text-red-400 hover:text-red-300 hover:bg-red-900/30 h-8 w-8 p-0"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
              
              {/* Resumen total */}
              {myRecords.length > 0 && (
                <Card className="card-mk border-gray-800 bg-gradient-to-br from-gray-900 to-gray-800">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-gold">{myRecords.length}</p>
                        <p className="text-xs text-gray-400">Total registros</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-white">{Object.keys(myRecordsByEntreno).length}</p>
                        <p className="text-xs text-gray-400">Entrenos</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-400">
                          {myRecords.filter(r => {
                            const pos = sortedRecords.findIndex(sr => sr.id === r.id);
                            return pos < 3;
                          }).length}
                        </p>
                        <p className="text-xs text-gray-400">Top 3</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Dialog añadir récord */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-gold" />
                Añadir Tiempo
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {isAdmin && (
                <div>
                  <Label className="text-gray-400">Usuario</Label>
                  <Select 
                    value={formData.user_id || "self"} 
                    onValueChange={(value) => setFormData({ ...formData, user_id: value === "self" ? "" : value })}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                      <SelectValue placeholder="Yo mismo" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="self" className="text-white">Yo mismo</SelectItem>
                      {Object.values(usuarios)
                        .filter(u => u.role === 'miembro')
                        .map(u => (
                          <SelectItem key={u.id} value={u.id} className="text-white">
                            {u.nombre}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label className="text-gray-400">Entreno *</Label>
                <Select 
                  value={formData.entreno_id} 
                  onValueChange={(value) => setFormData({ ...formData, entreno_id: value })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue placeholder="Seleccionar entreno" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {hofEntrenos.map(e => (
                      <SelectItem key={e.id} value={e.id} className="text-white">
                        {e.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400">
                    {(() => {
                        const e = hofEntrenos.find(ent => ent.id === formData.entreno_id);
                        return e?.tipo === 'reps' ? 'Repeticiones *' : 'Tiempo * (MM:SS)';
                    })()}
                  </Label>
                  <Input
                    value={formData.tiempo}
                    onChange={(e) => setFormData({ ...formData, tiempo: e.target.value })}
                    placeholder={(() => {
                        const e = hofEntrenos.find(ent => ent.id === formData.entreno_id);
                        return e?.tipo === 'reps' ? 'Ej: 50' : '3:45';
                    })()}
                    className="bg-gray-800 border-gray-700 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Fecha</Label>
                  <Input
                    type="date"
                    value={formData.fecha}
                    onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-gray-400">Notas (opcional)</Label>
                <Input
                  value={formData.notas}
                  onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  placeholder="Ej: Récord personal"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddDialog(false)}
                  className="flex-1 border-gray-700 text-gray-300"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 btn-primary-mk"
                  disabled={!formData.entreno_id || !formData.tiempo}
                >
                  Guardar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog gestionar entrenos */}
        <Dialog open={showManageEntrenosDialog} onOpenChange={setShowManageEntrenosDialog}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Dumbbell className="h-5 w-5 text-gold" />
                Gestionar Entrenos
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {hofEntrenos.length === 0 ? (
                  <p className="text-gray-400 text-center py-4">No hay entrenos configurados</p>
                ) : (
                  hofEntrenos.map(entreno => (
                      <div 
                      key={entreno.id}
                      className="flex items-center justify-between p-3 bg-gray-800 rounded-lg"
                    >
                      <div>
                          <p className="text-white font-medium">{entreno.nombre}</p>
                          <p className="text-xs text-gray-400 capitalize">{entreno.tipo === 'reps' ? 'Repeticiones' : 'Tiempo'}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEntreno(entreno.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>

              <div className="border-t border-gray-800 pt-4">
                <Label className="text-gray-400 mb-2 block">Añadir nuevo entreno</Label>
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Input
                        value={newEntrenoName}
                        onChange={(e) => setNewEntrenoName(e.target.value)}
                        placeholder="Nombre del entreno"
                        className="bg-gray-800 border-gray-700 text-white flex-1"
                    />
                    <Select value={newEntrenoType} onValueChange={setNewEntrenoType}>
                        <SelectTrigger className="w-[140px] bg-gray-800 border-gray-700 text-white">
                        <SelectValue placeholder="Tipo" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                        <SelectItem value="tiempo" className="text-white">Por Tiempo</SelectItem>
                        <SelectItem value="reps" className="text-white">Por Reps</SelectItem>
                        </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleAddEntreno}
                    disabled={!newEntrenoName.trim()}
                    className="btn-primary-mk w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" /> Crear Entreno
                  </Button>
                </div>
              </div>

              <Button
                variant="outline"
                onClick={() => setShowManageEntrenosDialog(false)}
                className="w-full border-gray-700 text-gray-300"
              >
                Cerrar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default HallOfFame;
